import { Component, computed, effect, inject, input, linkedSignal, output, untracked } from '@angular/core';
import { Button } from '../button/button';
import { Spinner } from '../spinner/spinner';
import { CommasPipe } from '@pipes/commas-pipe';
import { Translate } from '@services/core/translate';
import { format } from '@helpers';
import type { PaginationResult } from '@appTypes';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [ Button, Spinner, CommasPipe ],
  templateUrl: './pagination.html',
  styleUrl: './pagination.scss',
})
export class Pagination<T> {
  // Dependencias
  public readonly t = inject(Translate).translateFn();

  // Inputs
  $items = input.required<T[], T[]>({ alias: 'items', transform: v =>  [ ...v ] });
  $itemsPerPage = input<number>(10, { alias: 'itemsPerPage' });
  $_currentPage = input<number>(1, { alias: 'currentPage' });
  $currentPage = linkedSignal(this.$_currentPage);
  $loading = input<boolean>(false, { alias: 'loading' });

  // Outputs
  pageChange = output<PaginationResult<T>>();

  // Signals / atributos
  fromIndex: number = 0;
  toIndex: number = 0;

  // Signals computados
  $pagesAmount = computed<number>(() => Math.ceil(this.$items().length / this.$itemsPerPage()));

  $registriesAmountText = computed<string>(() => {
    let text: string;
    if (this.$visibleItems().length === 1)
      text = format(this.t('record_of_on')(), this.fromIndex + 1, this.$items().length, this.$pagesAmount());
    else
      text = format(this.t('records_to_of_on')(), this.fromIndex + 1, this.toIndex, this.$items().length, this.$pagesAmount());
    return text;
  });

  $leftValue = computed<number>(() => {
    if (this.$currentPage() === 1 || this.$pagesAmount() < 4) return 1;
    if (this.$currentPage() === this.$pagesAmount()) return this.$pagesAmount() - 2;
    return this.$currentPage() - 1;
  });

  $middleValue = computed<number>(() => {
    if (this.$currentPage() === 1 || this.$pagesAmount() < 4) return 2;
    if (this.$currentPage() === this.$pagesAmount()) return this.$pagesAmount() - 1;
    return this.$currentPage();
  });

  $rightValue = computed<number>(() => {
    if (this.$currentPage() === 1 || this.$pagesAmount() < 4) return 3;
    if (this.$currentPage() === this.$pagesAmount()) return this.$pagesAmount();
    return this.$currentPage() + 1;
  });

  $visibleItems = computed<T[]>(() => {
    const items = this.$items();
    const itemsPerPage = this.$itemsPerPage();
    const currentPage = this.$currentPage();
    this.fromIndex = (currentPage - 1) * itemsPerPage;
    this.toIndex = Math.min(currentPage * itemsPerPage, items.length);
    return items.slice(this.fromIndex, this.toIndex);
  });

  // Emitir arreglo de items visibles
  constructor() {
    effect(() => {
      this.pageChange.emit({ page: untracked(this.$currentPage), items: this.$visibleItems() });
    });
  }

  // Cambia la página actual hacia el nuevo objetivo
  changePage(target: 'previous' | 'next' | number) {
    switch (target) {
      case 'previous':
        this.$currentPage.update(value => value - 1);
        break;
      case 'next':
        this.$currentPage.update(value => value + 1);
        break;
      default:
        this.$currentPage.set(target);
    }
  }
}
